import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';
import { Device, DevicesApiResponse, MerchantProfile, MerchantProfileApiResponse, CurrentSession } from '../components/Analytics/ActiveSessionsAndDevices/types';

interface ActiveSessionsAndDevicesState {
  devices: Device[];
  currentSessions: CurrentSession[];
  merchantProfile: MerchantProfile | null;
  loading: boolean;
  error: string | null;
}

const initialState: ActiveSessionsAndDevicesState = {
  devices: [],
  currentSessions: [],
  merchantProfile: null,
  loading: false,
  error: null,
};

// Fetch devices
export const fetchDevices = createAsyncThunk<
  Device[],
  void,
  { rejectValue: string }
>('activeSessionsAndDevices/fetchDevices', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get<DevicesApiResponse>('/merchant/devices', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('No devices data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch devices'
    );
  }
});

// Fetch merchant profile (for current sessions)
export const fetchMerchantProfile = createAsyncThunk<
  MerchantProfile,
  void,
  { rejectValue: string }
>('activeSessionsAndDevices/fetchMerchantProfile', async (_, { rejectWithValue }) => {
  const role = localStorage.getItem('role');
  if (role !== 'merchant') {
    return rejectWithValue('Merchant profile is only for merchant role');
  }
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get<MerchantProfileApiResponse>('merchant/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.success && response.data.data) {
      const now = Date.now();
      localStorage.setItem('lastUpdatedTimestamp', now.toString());
      return response.data.data;
    } else {
      return rejectWithValue('No merchant profile data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchant profile'
    );
  }
});

// Fetch all data (devices + merchant profile)
export const fetchAllData = createAsyncThunk<
  { devices: Device[]; merchantProfile: MerchantProfile },
  void,
  { rejectValue: string }
>('activeSessionsAndDevices/fetchAllData', async (_, { rejectWithValue }) => {
  const role = localStorage.getItem('role');
  if (role !== 'merchant') {
    return rejectWithValue('Merchant profile is only for merchant role');
  }
  try {
    const token = localStorage.getItem('access_token');
    
    const [devicesResponse, profileResponse] = await Promise.all([
      API.get<DevicesApiResponse>('/merchant/devices', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      API.get<MerchantProfileApiResponse>('merchant/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    ]);

    const devices = devicesResponse.data?.success && devicesResponse.data?.data
      ? devicesResponse.data.data
      : [];

    const merchantProfile = profileResponse.data?.success && profileResponse.data?.data
      ? profileResponse.data.data
      : null;

    if (!merchantProfile) {
      return rejectWithValue('No merchant profile data received from server');
    }

    const now = Date.now();
    localStorage.setItem('lastUpdatedTimestamp', now.toString());

    return { devices, merchantProfile };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch data'
    );
  }
});

const activeSessionsAndDevicesSlice = createSlice({
  name: 'activeSessionsAndDevices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.devices = [];
      state.currentSessions = [];
      state.merchantProfile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch devices
      .addCase(fetchDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload;
        state.error = null;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch devices';
        state.devices = [];
      })
      // Fetch merchant profile
      .addCase(fetchMerchantProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.merchantProfile = action.payload;
        state.currentSessions = action.payload.currentSessions || [];
        state.error = null;
      })
      .addCase(fetchMerchantProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch merchant profile';
        state.currentSessions = [];
      })
      // Fetch all data
      .addCase(fetchAllData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllData.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload.devices;
        state.merchantProfile = action.payload.merchantProfile;
        state.currentSessions = action.payload.merchantProfile.currentSessions || [];
        state.error = null;
      })
      .addCase(fetchAllData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch data';
        state.devices = [];
        state.currentSessions = [];
      });
  },
});

export const { clearError, clearData } = activeSessionsAndDevicesSlice.actions;
export default activeSessionsAndDevicesSlice.reducer;
